import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useNavigate } from 'react-router'
import { usePostLoginNavigation } from 'src/legacy_modules/auth/login/hooks/usePostLoginNavigation'

import { Agreement } from './agreement'
import { AgreementType } from './entity'
import { useAgreement } from './hooks/use-agreement'
import { routes } from '../../routes'

export type AgreementItem = {
  agreementType: AgreementType
  checked: boolean
  onChange: () => void
  enabled: boolean
  onEnable: () => void
  path: string
}

const pathByAgreementType: Record<AgreementType, string> = {
  privacyPolicy: routes.privacyPolicy,
  termsOfService: routes.termsOfService,
  externalTransmissionPolicy: routes.externalTransmissionPolicy,
}

export const AgreementContainer: React.FC = () => {
  const navigate = useNavigate()
  const { fetchAgreements, submit, requesting, errorMessage } = useAgreement()
  const { jumpAfterLogin } = usePostLoginNavigation()

  const [requiredAgreementTypes, setRequiredAgreementTypes] = useState<
    AgreementType[]
  >([])
  const [checked, setChecked] = useState(new Map<AgreementType, boolean>())
  const [enabled, setEnabled] = useState(new Map<AgreementType, boolean>())
  const [enabledSubmit, setEnabledSubmit] = useState(false)

  useEffect(() => {
    // すべてチェックされているか確認
    const allChecked = Array.from(checked.values()).every(e => e)
    setEnabledSubmit(allChecked && !requesting)
  }, [checked, requesting])

  useEffect(() => {
    if (requesting) return
    const get = async () => {
      const agreements = await fetchAgreements()

      const agreementTypes = [
        agreements.agreeTermsOfService ? null : 'termsOfService',
        agreements.agreePrivacyPolicy ? null : 'privacyPolicy',
        agreements.agreeExternalTransmissionPolicy
          ? null
          : 'externalTransmissionPolicy',
      ].filter(e => e !== null) as AgreementType[]

      // すべて同意済みであれば、ログイン画面に遷移
      if (agreementTypes.length === 0) {
        navigate(routes.login)
      }

      // 必要なenabled, checkedを初期化
      agreementTypes.forEach(agreementType => {
        setChecked(prev => {
          const next = new Map(prev)
          next.set(agreementType, false)
          return next
        })
        setEnabled(prev => {
          const next = new Map(prev)
          next.set(agreementType, false)
          return next
        })
      })

      setRequiredAgreementTypes(agreementTypes)
    }
    get()
  }, [requesting, fetchAgreements, navigate])

  const agreementItems = useMemo<AgreementItem[]>(
    () =>
      requiredAgreementTypes.map(t => ({
        agreementType: t,
        checked: checked.get(t) || false,
        onChange: () => {
          setChecked(prev => {
            const next = new Map(prev)
            next.set(t, !prev.get(t))
            return next
          })
        },
        enabled: enabled.get(t) || false,
        onEnable: () => {
          setEnabled(prev => {
            const next = new Map(prev)
            next.set(t, true)
            return next
          })
        },
        path: pathByAgreementType[t],
      })),
    [checked, enabled, requiredAgreementTypes],
  )

  const onSubmit = useCallback(async () => {
    await submit({
      // チェックしたもののみ同意として送信
      agreeTermsOfService: checked.get('termsOfService'),
      agreePrivacyPolicy: checked.get('privacyPolicy'),
      agreeExternalTransmissionPolicy: checked.get(
        'externalTransmissionPolicy',
      ),
    })
    await jumpAfterLogin()
  }, [checked, submit, jumpAfterLogin])

  return (
    <Agreement
      agreementItems={agreementItems}
      onSubmit={onSubmit}
      enabledSubmit={enabledSubmit}
      errorMessage={errorMessage || undefined}
    />
  )
}
